import React, { useState } from "react";
import { Button, Card, Col, Row, Stack } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { deleteGuardian, deleteMember, postNote, patchGuardian, patchMember, patchCase } from "../../services/httpClient";
import DatePicker from "../ui/DatePicker";
import SelectObject from "../ui/SelectObject";
import Textarea from "../ui/Textarea";

interface DeleteState {
  setIsDeleting: any;
  user: any;
  family: any[];
  fetchHousehold: any;
  resetHouseholdTab: any;
  caseStatus: string | undefined;
  isLastUser: boolean;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

const ConfirmDelete = ({
  setIsDeleting,
  user,
  family,
  fetchHousehold,
  resetHouseholdTab,
  caseStatus,
  isLastUser,
  setShowToast,
  setToastText,
  setToastBody,
}: DeleteState) => {
  const { id } = useParams();
  const [exitDate, setExitDate] = useState<string>(new Date().toISOString().split("T")[0]);
  const [exitNote, setExitNote] = useState<string>();
  const [exitType, setExitType] = useState<string>("voluntary");
  const [allowDelete, setAllowDelete] = useState<boolean>(false);
  const [newMainContact, setNewMainContact] = useState<string>("");
  const name = user.firstname + " " + (user.lastname ?? "");
  const isPrimary = family.find((g: any) => g.id === user?.id)?.is_primary;
  const reassignMainContact = !!(isPrimary && (family?.filter((g: any) => g.id !== user?.id && g.role === "family").length || 0) > 0);

  const mainContactOptions = family
    .filter((g: any) => g.id !== user?.id && g.role === "family")
    .map((g: any) => ({
      id: g.id,
      key: g.id,
      name: `${g.firstname} ${g.lastname ?? ""}`,
    }));

  var exitTypes: any[] = [
    { id: 0, key: "voluntary", name: "Voluntary" },
    { id: 1, key: "involuntary", name: "Involuntary" },
  ];

  const onClickDeleteFinalWExit = () => {
    setIsDeleting(false);

    const deleteUserAndSaveNote = () => {
      if (user?.role === "family") {
        if (caseStatus === "active") {
          const note = {
            notetype: "exit",
            subject: {
              id: user?.id,
              type: "guardian",
            },
            fields: {
              exit_type: exitType,
              description: exitNote ?? "",
              date: new Date().toISOString().split("T")[0],
            },
          };

          // save exit note
          postNote(note, id)
            .then((res: any) => {
              const noteId = res[0].data.note_id;

              const exitData = {
                exit_at: exitDate,
                exit_note_id: noteId,
              };

              deleteGuardian(id, user?.id, exitData)
                .then((res: any) => {
                  resetHouseholdTab();
                  fetchHousehold();
                  setShowToast(true);
                  setToastText("Member Deleted");
                  setToastBody(name);
                })
                .catch((err: any) => {
                  console.log(err);
                });
            })
            .catch((err: any) => {
              console.log(err);
            });
        }
      } else {
        if (caseStatus === "active") {
          const note = {
            notetype: "exit",
            subject: {
              id: user?.id,
              type: "member",
            },
            fields: {
              exit_type: exitType,
              description: exitNote ?? "",
              date: new Date().toISOString().split("T")[0],
            },
          };

          // save exit note
          postNote(note, id)
            .then((res: any) => {
              const noteId = res[0].data.note_id;

              const exitData = {
                exit_at: exitDate,
                exit_note_id: noteId,
              };

              deleteMember(id, user?.id, exitData)
                .then((res: any) => {
                  resetHouseholdTab();
                  fetchHousehold();
                  setShowToast(true);
                  setToastText("Member Deleted");
                  setToastBody(name);
                })
                .catch((err: any) => {
                  console.log(err);
                });
            })
            .catch((err: any) => {
              console.log(err);
            });
        }
      }
    };

    // reassign main contact
    if (user?.role === "family" && isPrimary && newMainContact) {
      const updated = { is_primary: true };
      patchGuardian(updated, id, newMainContact)
        .then(() => deleteUserAndSaveNote())
        .catch((err: any) => {
          console.log(err);
          deleteUserAndSaveNote();
        });
    } else {
      deleteUserAndSaveNote();
    }
  };

  const onClickDeleteFinal = () => {
    setIsDeleting(false);

    const deleteUser = () => {
      if (user?.role === "family") {
        deleteGuardian(id, user?.id)
          .then((res: any) => {
            resetHouseholdTab();
            fetchHousehold();
            setShowToast(true);
            setToastText("Member Deleted");
            setToastBody(name);
          })
          .catch((err: any) => {
            console.log(err);
          });
      } else {
        deleteMember(id, user?.id)
          .then((res: any) => {
            resetHouseholdTab();
            fetchHousehold();
            setShowToast(true);
            setToastText("Member Deleted");
            setToastBody(name);
          })
          .catch((err: any) => {
            console.log(err);
          });
      }
    };

    // reassign main contact
    if (user?.role === "family" && isPrimary && newMainContact) {
      const updated = { is_primary: true };
      patchGuardian(updated, id, newMainContact)
        .then(() => deleteUser())
        .catch((err: any) => {
          console.log(err);
          deleteUser();
        });
    } else {
      deleteUser();
    }
  };

  const onClickDeactivateCase = async () => {
    // Deactivate the case
    await patchCase({ casestatus: "inactive" }, id);

    // create exit note for the current user
    if (caseStatus === "active") {
      const note = {
        notetype: "exit",
        subject: {
          id: user?.id,
          type: user?.role === "family" ? "guardian" : "member",
        },
        fields: {
          exit_type: exitType,
          description: exitNote ?? "",
          date: new Date().toISOString().split("T")[0],
        },
      };
      const res = await postNote(note, id);
      const noteId = res[0].data.note_id;

      const exitData = {
        exit_at: exitDate,
        exit_note_id: noteId,
      };

      if (user?.role === "family") {
        await patchGuardian(exitData, id, user?.id);
      } else {
        await patchMember(exitData, id, user?.id);
      }
    }

    setShowToast(true);
    setToastText("Case Deactivated");
    setToastBody(`Case has been made inactive`);
    fetchHousehold();
    resetHouseholdTab();
  };

  const updateExitNote = (e: any) => {
    setExitNote(e.target.value);
    setAllowDelete(true);
  };

  const updateExitType = (e: any) => {
    setExitType(e.target.value);
  };

  const onClickCancelDelete = () => {
    setIsDeleting(false);
  };

  return (
    <Card className="bg-light mb-3">
      <Card.Body>
        {isLastUser ? (
          <>
            <Row>
              <Col xs={12}>
                <Stack direction="horizontal" className="mb-3">
                  <Col xs={6}>
                    <Card.Subtitle>
                      {"You cannot delete the last person. "}
                      {caseStatus === "active" && "Would you like to change case status to Inactive?"}
                    </Card.Subtitle>
                  </Col>
                  <Col xs={6} className="d-flex justify-content-end">
                    <Stack direction="horizontal" gap={3}>
                      {caseStatus === "active" && (
                        <>
                          <Button className="app-danger-color button text-nowrap" onClick={onClickDeactivateCase}>
                            Deactivate Case
                          </Button>
                          <div className="vr" />
                        </>
                      )}
                      <Button className="app-primary-color button" onClick={onClickCancelDelete}>
                        Cancel
                      </Button>
                    </Stack>
                  </Col>
                </Stack>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col xs={12}>
                <Textarea label={"Exit Note"} value={exitNote} hasFocus={true} rows={1} onUpdate={(e: any) => updateExitNote(e)} css={""} />
              </Col>
            </Row>
            <Row>
              <Col xs={8}>
                <SelectObject
                  label="Exit Type"
                  value={exitType}
                  options={exitTypes}
                  onUpdate={(e: any) => updateExitType(e)}
                  skipInitialOption={true}
                />
              </Col>
              <Col xs={4}>
                <DatePicker label="Exit Date" currentValue={exitDate} setCurrentValue={setExitDate} hasMaxDate={true} />
              </Col>
            </Row>
          </>
        ) : (
          <>
            <Row>
              <Col xs={12}>
                <Stack direction="horizontal" className={caseStatus === "active" ? "mb-3" : ""}>
                  <Col xs={6}>
                    <Card.Subtitle>Are you sure you want to delete {name}?</Card.Subtitle>
                  </Col>
                  <Col xs={6} className="d-flex justify-content-end">
                    <Stack direction="horizontal" gap={3}>
                      <Button
                        className="app-danger-color button"
                        onClick={() => {
                          caseStatus === "active" ? onClickDeleteFinalWExit() : onClickDeleteFinal();
                        }}
                        disabled={!allowDelete}
                      >
                        Confirm Delete
                      </Button>
                      <div className="vr" />
                      <Button className="app-primary-color button" onClick={() => onClickCancelDelete()}>
                        Cancel
                      </Button>
                    </Stack>
                  </Col>
                </Stack>
              </Col>
            </Row>
            {caseStatus === "active" && (
              <>
                <Row className="mb-3">
                  <Col xs={12}>
                    <Textarea
                      label={"Exit Note"}
                      value={exitNote}
                      hasFocus={true}
                      rows={1}
                      onUpdate={(e: any) => updateExitNote(e)}
                      css={""}
                    />
                  </Col>
                </Row>
                <Row>
                  {reassignMainContact && (
                    <Col xs={4}>
                      <SelectObject
                        label="New Main Contact"
                        initialOption="None"
                        value={newMainContact}
                        options={mainContactOptions}
                        onUpdate={(e: any) => setNewMainContact(e.target.value)}
                      />
                    </Col>
                  )}
                  <Col xs={reassignMainContact ? 4 : 8}>
                    <SelectObject
                      label="Exit Type"
                      value={exitType}
                      options={exitTypes}
                      onUpdate={(e: any) => updateExitType(e)}
                      skipInitialOption={true}
                    />
                  </Col>
                  <Col xs={4}>
                    <DatePicker label="Exit Date" currentValue={exitDate} setCurrentValue={setExitDate} hasMaxDate={true} />
                  </Col>
                </Row>
              </>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default ConfirmDelete;
